// Reader-facing rendering of an "images" article section: a single photo, or a
// swipeable slider when the section holds more than one.

import { useState } from 'react'
import { getChapterImageUrl, fallbackToOriginalOnError } from '@/shared/lib/imageUtils'
import { useDragScroll } from '@/shared/hooks/useDragScroll'
import type { SectionImage } from '@/shared/lib/articleSections'

type ArticleImageSliderProps = {
  images: SectionImage[]
  /** Falls back to this when a photo has no caption of its own. */
  alt?: string
  className?: string
}

function Caption({ text }: { text: string }) {
  return (
    <p className="mt-2 px-1 text-xs leading-relaxed text-foreground/50 dark:text-white/75">
      {text}
    </p>
  )
}

export function ArticleImageSlider({ images, alt, className = '' }: ArticleImageSliderProps) {
  const { scrollRef, onMouseDown } = useDragScroll()
  const [activeIndex, setActiveIndex] = useState(0)

  if (images.length === 0) return null

  if (images.length === 1) {
    const [image] = images
    return (
      <figure className={className}>
        <img
          src={getChapterImageUrl(image.file_id)}
          alt={image.caption || alt || ''}
          className="w-full rounded-2xl object-cover"
          onError={fallbackToOriginalOnError}
        />
        {image.caption && <Caption text={image.caption} />}
      </figure>
    )
  }

  // Native scroll-snap rather than a JS carousel: swiping is what readers expect on
  // mobile, and it keeps working without JS-driven animation on desktop.
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const index = Math.round(el.scrollLeft / el.clientWidth)
    setActiveIndex(Math.min(Math.max(index, 0), images.length - 1))
  }

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' })
  }

  const activeCaption = images[activeIndex]?.caption

  return (
    <figure className={className}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        onMouseDown={onMouseDown}
        className="flex w-full snap-x snap-mandatory overflow-x-auto rounded-2xl cursor-grab select-none active:cursor-grabbing"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {images.map((image, index) => (
          <img
            key={`${image.file_id}-${index}`}
            src={getChapterImageUrl(image.file_id)}
            alt={image.caption || alt || ''}
            className="w-full shrink-0 snap-center object-cover"
            decoding="async"
            draggable={false}
            onError={fallbackToOriginalOnError}
          />
        ))}
      </div>

      <div className="mt-2 flex justify-center gap-1.5">
        {images.map((image, index) => (
          <button
            key={`${image.file_id}-${index}-dot`}
            type="button"
            onClick={() => scrollToIndex(index)}
            aria-label={`Bild ${index + 1} von ${images.length}`}
            aria-current={index === activeIndex}
            className={`h-1.5 rounded-full transition-all ${
              index === activeIndex ? 'w-4 bg-primary' : 'w-1.5 bg-foreground/20 dark:bg-white/25'
            }`}
          />
        ))}
      </div>

      {/* Reserve the caption line so the following text doesn't jump while swiping
          between photos with and without a caption. */}
      <div className="min-h-[1.25rem]">{activeCaption && <Caption text={activeCaption} />}</div>
    </figure>
  )
}
